import axios from 'axios';

// Create axios instance
const api = axios.create({
    baseURL: `${process.env.REACT_APP_API_URL}/api`,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor
api.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        // Check if error response exists and status is 401 (Unauthorized)
        if (error.response && error.response.status === 401) {
            // Clear local storage
            localStorage.removeItem('token');
            localStorage.removeItem('user');

            // Redirect to login page if not already there
            if (!window.location.pathname.includes('/login')) {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

/**
 * Authentication API endpoints
 */
export const authAPI = {
    /**
     * Register a new user
     * @param {Object} data - User registration data
     * @returns {Promise} Axios response
     */
    register: (data) => api.post('/auth/register', data),

    /**
     * Login user
     * @param {Object} data - User login credentials
     * @returns {Promise} Axios response
     */
    login: (data) => api.post('/auth/login', data),

    /**
     * Get current authenticated user
     * @returns {Promise} Axios response
     */
    getCurrentUser: () => api.get('/auth/me'),

    /**
     * Update user profile (auth context)
     * @param {Object} data - Profile update data
     * @returns {Promise} Axios response
     */
    updateProfile: (data) => api.put('/auth/profile', data),

    /**
     * Mark user as onboarded
     * @returns {Promise} Axios response
     */
    completeOnboarding: () => api.put('/auth/onboarding/complete'),
};

/**
 * Leads API endpoints
 */
export const leadsAPI = {
    /**
     * Get leads with optional filtering
     * @param {Object} params - Query parameters for filtering (page, limit, status, search)
     * @returns {Promise} Axios response
     */
    getLeads: (params) => api.get('/leads', { params }),

    /**
     * Get a single lead by ID
     * @param {string} id - Lead ID
     * @returns {Promise} Axios response
     */
    getLeadById: (id) => api.get(`/leads/${id}`),

    /**
     * Create a new lead
     * @param {Object} data - Lead data
     * @returns {Promise} Axios response
     */
    createLead: (data) => api.post('/leads', data),

    /**
     * Bulk create leads
     * @param {Array} leads - Array of lead objects
     * @returns {Promise} Axios response
     */
    bulkCreateLeads: (leads) => api.post('/leads/bulk', { leads }),

    /**
     * Update lead status
     * @param {string} id - Lead ID
     * @param {string} status - New status
     * @returns {Promise} Axios response
     */
    updateLeadStatus: (id, status) => api.put(`/leads/${id}/status`, { status }),

    /**
     * Delete a single lead
     * @param {string} id - Lead ID
     * @returns {Promise} Axios response
     */
    deleteLead: (id) => api.delete(`/leads/${id}`),

    /**
     * Bulk delete multiple leads
     * @param {string[]} ids - Array of Lead IDs
     * @returns {Promise} Axios response
     */
    deleteLeads: (ids) => api.delete('/leads/bulk', { data: { ids } }),

    /**
     * Get lead statistics
     * @returns {Promise} Axios response
     */
    getStats: (params) => api.get('/leads/stats', { params }),

    /**
     * Get all notes for a specific lead
     * @param {string} id - Lead ID
     * @returns {Promise} Axios response
     */
    getNotes: (id) => api.get(`/leads/${id}/notes`),

    /**
     * Add a note to a specific lead
     * @param {string} id - Lead ID
     * @param {string} note - Note content
     * @returns {Promise} Axios response
     */
    addNote: (id, note) => api.post(`/leads/${id}/notes`, { note }),

    /**
     * Delete a specific note
     * @param {string} noteId - Note ID
     * @returns {Promise} Axios response
     */
    deleteNote: (noteId) => api.delete(`/leads/notes/${noteId}`),
};

/**
 * Templates API endpoints
 */
export const templatesAPI = {
    /**
     * Get all templates
     * @returns {Promise} Axios response
     */
    getTemplates: () => api.get('/templates'),

    /**
     * Get template by ID
     * @param {string} id - Template ID
     * @returns {Promise} Axios response
     */
    getTemplateById: (id) => api.get(`/templates/${id}`),

    /**
     * Create a new template
     * @param {Object} data - Template data
     * @returns {Promise} Axios response
     */
    createTemplate: (data) => api.post('/templates', data),

    /**
     * Update an existing template
     * @param {string} id - Template ID
     * @param {Object} data - Updated template data
     * @returns {Promise} Axios response
     */
    updateTemplate: (id, data) => api.put(`/templates/${id}`, data),

    /**
     * Delete a template
     * @param {string} id - Template ID
     * @returns {Promise} Axios response
     */
    deleteTemplate: (id) => api.delete(`/templates/${id}`),

    /**
     * Set a template as default
     * @param {string} id - Template ID
     * @returns {Promise} Axios response
     */
    setDefaultTemplate: (id) => api.put(`/templates/${id}/set-default`),
};

/**
 * Channels API endpoints
 */
export const channelsAPI = {
    connectGmail: () => api.get('/channels/gmail/connect'),
    getGmailStatus: () => api.get('/channels/gmail/status'),
    testGmailConnection: () => api.get('/channels/gmail/test'),
    disconnectGmail: () => api.delete('/channels/gmail/disconnect'),

    // Microsoft / Outlook
    startMicrosoftAuth: () => api.get('/channels/microsoft/auth'),
    getMicrosoftStatus: () => api.get('/channels/microsoft/status'),
    disconnectMicrosoft: () => api.delete('/channels/microsoft/disconnect'),

    getTwilioStatus: () => api.get('/channels/twilio/status'),
    getWhatsAppStatus: () => api.get('/channels/whatsapp/status'),
    getAllChannels: () => api.get('/channels'),

    // IMAP Email
    connectIMAP: (data) => api.post('/channels/email/connect-imap', data),
    getIMAPStatus: () => api.get('/channels/email/imap-status'),
    testIMAPConnection: () => api.get('/channels/email/test-imap'),
    disconnectIMAP: () => api.delete('/channels/email/disconnect-imap'),
};

/**
 * Microsoft API endpoints
 */
export const microsoftAPI = {
    startAuth: () => api.get('/channels/microsoft/auth'),
    getStatus: () => api.get('/channels/microsoft/status'),
    testConnection: () => api.get('/channels/microsoft/test'),
    disconnect: () => api.delete('/channels/microsoft/disconnect')
};

/**
 * Settings API endpoints
 */
export const settingsAPI = {
    /**
     * Get user profile settings
     * @returns {Promise} Axios response
     */
    getProfile: () => api.get('/settings/profile'),

    /**
     * Update user profile settings
     * @param {Object} data - Profile data
     * @returns {Promise} Axios response
     */
    updateProfile: (data) => api.put('/settings/profile', data),

    /**
     * Upload user avatar
     * @param {File} file - Image file
     * @returns {Promise} Axios response
     */
    uploadAvatar: (file) => {
        const formData = new FormData();
        formData.append('avatar', file);
        return api.post('/settings/profile/avatar', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    /**
     * Get lead source settings
     * @returns {Promise} Axios response
     */
    getLeadSources: () => api.get('/settings/lead-sources'),

    /**
     * Update lead source configuration
     * @param {Object} data - Lead source data ({ enabledPlatforms: [] })
     * @returns {Promise} Axios response
     */
    updateLeadSources: (data) => api.put('/settings/lead-sources', data),

    getLeadSourceStats: () => api.get('/settings/lead-source-stats'),

    /**
     * Get notification preferences
     * @returns {Promise} Axios response
     */
    getNotificationPreferences: () => api.get('/settings/notifications'),

    /**
     * Update notification preferences
     * @param {Object} data - Notification preferences
     * @returns {Promise} Axios response
     */
    updateNotificationPreferences: (data) => api.put('/settings/notifications', data),

    /**
     * Get usage statistics
     * @returns {Promise} Axios response
     */
    getUsage: () => api.get('/settings/usage'),

    /**
     * Get billing history
     * @param {Object} params - Query parameters (page, limit)
     * @returns {Promise} Axios response
     */
    getBillingHistory: (params) => api.get('/settings/billing-history', { params }),
};

/**
 * Notifications API endpoints
 */
export const notificationsAPI = {
    /**
     * Get unread notifications count
     * @returns {Promise} Axios response
     */
    getUnreadCount: () => api.get('/notifications/unread-count'),

    /**
     * Get all notifications
     * @param {Object} params - Query parameters (page, limit)
     * @returns {Promise} Axios response
     */
    getNotifications: (params) => api.get('/notifications', { params }),

    /**
     * Mark notification as read
     * @param {string} id - Notification ID
     * @returns {Promise} Axios response
     */
    markAsRead: (id) => api.put(`/notifications/${id}/read`),

    /**
     * Mark all notifications as read
     * @returns {Promise} Axios response
     */
    markAllAsRead: () => api.put('/notifications/read-all'),
};

/**
 * Billing API endpoints
 */
export const billingAPI = {
    getSubscription: () => api.get('/billing/subscription'),
    createCheckout: (plan, billingInterval) => api.post('/billing/checkout', { plan, billingInterval }),
    createPortal: () => api.post('/billing/portal'),
    cancelSubscription: () => api.post('/billing/cancel'),
};

/**
 * Analytics API endpoints
 */
export const analyticsAPI = {
    getStats: () => api.get('/analytics/stats'),
    getAdminStats: () => api.get('/analytics/admin-stats'),
};

export default api;
